/* eslint-disable react-refresh/only-export-components */
import React from "react";
import { Tree, TreeProps } from "primereact/tree";
import type { TreeNode } from "primereact/treenode";
import { Tooltip } from "primereact/tooltip";

type LessonType = "breakfix" | "video" | "HandsOnLesson" | "VideoLesson";

const LESSON_ICONS: Record<LessonType, string> = {
  video: "pi pi-video",
  VideoLesson: "pi pi-video",
  breakfix: "pi pi-code",
  HandsOnLesson: "pi pi-code",
};

type TooltipTreeNode = TreeNode & {
  description?: string;
  tooltipText?: string;
  sidebarShowMore?: boolean;
  onShowMoreClick?: () => void;
  averageScore?: number;
  lessonType?: LessonType;
  name?: string;
  customClassName?: string;
};

export interface PathTreeProps extends TreeProps {
  value: TooltipTreeNode[];
  selectionKeys: TreeProps["selectionKeys"];
  onSelectionChange?: TreeProps["onSelectionChange"];
  expandedKeys: TreeProps["expandedKeys"];
  onToggle?: TreeProps["onToggle"];
  className?: string;
  hasTooltips?: boolean;
  filterMode?: "lenient" | "strict" | undefined;
  filterPlaceholder?: string;
  sidebarShowMore?: boolean;
  onShowMoreClick?: (item: TooltipTreeNode) => void;
}

export const nodeTemplateFactory = (
  shouldShowMoreButton: boolean,
  onShowMoreClick: (node: TooltipTreeNode) => void
) => {
  // eslint-disable-next-line react/display-name
  return (node: TooltipTreeNode) => {
    const showMore = shouldShowMoreButton && node.description;
    const iconName = LESSON_ICONS[node.lessonType!] ?? "";

    return (
      <span className={`p-tree-node-content flex items-center ${node.customClassName || ""}`}>
        {iconName && <i className={iconName} style={{ marginRight: "0.5rem" }} />}

        <span>
          {node.label}
          {node.averageScore != null && ` (${node.averageScore}%)`}
        </span>

        {showMore && (
          <button
            type="button"
            className="p-button p-button-text p-sm p-ml-1 p-1 ml-1"
            onClick={() => onShowMoreClick(node)}
          >
            View Details
          </button>
        )}

        {node.tooltipText && (
          <>
            <Tooltip target={`.tooltip-icon-${node.id}`} style={{ backgroundColor: "#fff" }}>
              <p>{node.tooltipText}</p>
            </Tooltip>
            <i
              className={`pi pi-info-circle tooltip-icon-${node.id}`}
              style={{
                marginLeft: ".5rem",
                cursor: "pointer",
                backgroundColor: "#0071B6",
                color: "#fff",
                borderRadius: "50%",
              }}
            />
          </>
        )}
      </span>
    );
  };
};

export const PathTree: React.FC<PathTreeProps> = ({
  value,
  selectionKeys,
  onSelectionChange,
  expandedKeys,
  onToggle,
  filter = true,
  filterBy = "label",
  selectionMode = "checkbox",
  className = "",
  filterMode = "lenient",
  loading,
  sidebarShowMore = false,
  onShowMoreClick = () => {},
  filterPlaceholder = "Search content",
  ...rest
}) => {
  const wrappedNodeTemplate = React.useMemo(
    () => nodeTemplateFactory(sidebarShowMore, onShowMoreClick),
    [sidebarShowMore, onShowMoreClick]
  );

  return (
    <Tree
      value={value}
      selectionMode={selectionMode}
      selectionKeys={selectionKeys}
      onSelectionChange={onSelectionChange}
      expandedKeys={expandedKeys}
      onToggle={onToggle}
      filter={filter}
      filterBy={filterBy}
      className={className}
      filterMode={filterMode}
      loading={loading}
      filterPlaceholder={filterPlaceholder}
      nodeTemplate={sidebarShowMore && wrappedNodeTemplate}
      {...rest}
    />
  );
};
